import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subject, takeUntil } from 'rxjs';
import { CoachTaskService } from '../../../../core/services/coach-task.service';
import { IContentItem } from '../../../../core/models/content-item.model';

const MAX_LENGTH = 2000;

/**
 * Turning a piece of content into a task. The wording is prefilled from the title because that is
 * what a coach would type anyway, and stays editable because the reason for watching is what makes
 * the task worth reading.
 */
@Component({
  selector: 'coach-assign-content-modal',
  templateUrl: './assign-content-modal.component.html',
  styleUrl: './assign-content-modal.component.scss',
})
export class AssignContentModalComponent implements OnInit, OnDestroy {
  @Input() studentId!: number;
  @Input() periodId?: number | null;
  @Input() item!: IContentItem;

  fg: FormGroup = this.formBuilder.group({
    content: [null, [Validators.required, Validators.maxLength(MAX_LENGTH)]],
  });

  isSaving = false;
  isSubmitted = false;

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private taskService: CoachTaskService,
    private translate: TranslateService,
    private toastrService: ToastrService,
  ) {
  }

  ngOnInit(): void {
    const key = this.item.type === 'live-stream'
      ? 'students.videos.assign_default_live'
      : 'students.videos.assign_default_video';

    this.fg.patchValue({content: this.translate.instant(key, {title: this.item.title})});
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.fg.invalid || this.isSaving) {
      return;
    }

    const contentRef = this.item.type === 'video'
      ? {videoId: this.item.id, periodId: this.periodId}
      : {liveStreamId: this.item.id, periodId: this.periodId};

    this.isSaving = true;
    this.taskService.createTask(this.studentId, this.fg.value.content, contentRef)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isSaving = false))
      .subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('students.videos.assigned_toast'));
          this.modal.close(true);
        },
        error: () => this.toastrService.error(this.translate.instant('students.videos.assign_error')),
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
